import { Directive, ElementRef, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { NgModel } from '@angular/forms';

/**
 * Diretiva de máscara monetária tipo "shift right" (comportamento de caixa registradora).
 *
 * Uso em p-inputNumber:
 *   <p-inputNumber [(ngModel)]="valor" [monetaryMask]="2" ...>
 *
 * O parâmetro é o número de casas decimais.
 * Para 0 casas decimais (ex: PYG), o comportamento é de inteiro simples sem shift.
 *
 * Comportamento (casas decimais > 0):
 *   - Digitar "1"    → 0,01
 *   - Digitar "12"   → 0,12
 *   - Digitar "123"  → 1,23
 *   - Digitar "1234" → 12,34
 *   - Backspace      → remove último dígito
 */
@Directive({
  selector: 'p-inputNumber[monetaryMask]',
})
export class MonetaryMaskDirective implements OnInit, OnDestroy {
  /** Número de casas decimais. Para PYG = 0 (sem máscara shift). */
  @Input('monetaryMask') casasDecimais: number = 2;

  /** Buffer interno: sequência de dígitos sem separadores */
  private digitos: string = '';

  /** Referência ao input nativo interno do p-inputNumber */
  private inputNativo: HTMLInputElement | null = null;

  /** Observer para detectar quando o input nativo é renderizado */
  private mutationObserver: MutationObserver | null = null;

  constructor(
    private hostEl: ElementRef<HTMLElement>,
    @Optional() private ngModel: NgModel,
  ) {}

  ngOnInit(): void {
    // O p-inputNumber renderiza um <input> interno — precisamos encontrá-lo
    this.tentarVincularInputNativo();

    // Caso o input ainda não exista (renderização assíncrona), observar o DOM
    if (!this.inputNativo) {
      this.mutationObserver = new MutationObserver(() => {
        if (!this.inputNativo) {
          this.tentarVincularInputNativo();
        }
      });
      this.mutationObserver.observe(this.hostEl.nativeElement, {
        childList: true,
        subtree: true,
      });
    }
  }

  ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }

    if (this.inputNativo) {
      this.inputNativo.removeEventListener('keydown', this.onKeydown);
      this.inputNativo.removeEventListener('focus', this.onFocus);
      this.inputNativo.removeEventListener('paste', this.onPaste);
    }
  }

  private tentarVincularInputNativo(): void {
    const input = this.hostEl.nativeElement.querySelector('input') as HTMLInputElement | null;
    if (input && input !== this.inputNativo) {
      this.inputNativo = input;
      this.inputNativo.addEventListener('keydown', this.onKeydown);
      this.inputNativo.addEventListener('focus', this.onFocus);
      this.inputNativo.addEventListener('paste', this.onPaste);

      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
    }
  }

  /**
   * Ao focar: inicializar o buffer a partir do valor atual do ngModel.
   */
  private onFocus = (_event: FocusEvent): void => {
    // Para PYG (0 casas decimais), não aplica máscara shift
    if (this.casasDecimais === 0) {
      return;
    }

    const valorAtual = this.ngModel?.value ?? 0;
    this.digitos = this.valorParaDigitos(valorAtual);
  };

  /**
   * Intercepta teclas no input nativo.
   */
  private onKeydown = (event: KeyboardEvent): void => {
    // Para PYG (0 casas decimais), não intercepta
    if (this.casasDecimais === 0) {
      return;
    }

    const tecla = event.key;

    // Permite teclas de navegação/sistema passarem sem interferência
    if (this.ehTeclaPermitida(tecla)) {
      return;
    }

    // Bloqueia qualquer outra tecla que não seja dígito ou Backspace/Delete
    if (!/^\d$/.test(tecla) && tecla !== 'Backspace' && tecla !== 'Delete') {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    if (tecla === 'Backspace' || tecla === 'Delete') {
      this.removerUltimoDigito();
    } else if (/^\d$/.test(tecla)) {
      this.adicionarDigito(tecla);
    }
  };

  /**
   * Bloqueia colar texto com formatação não numérica.
   */
  private onPaste = (event: ClipboardEvent): void => {
    if (this.casasDecimais === 0) {
      return;
    }

    event.preventDefault();
    const texto = event.clipboardData?.getData('text') || '';
    const apenasDigitos = texto.replace(/\D/g, '');

    for (const digito of apenasDigitos) {
      this.adicionarDigito(digito);
    }
  };

  /**
   * Adiciona um dígito ao buffer e atualiza o valor.
   */
  private adicionarDigito(digito: string): void {
    // Limitar a um tamanho razoável (15 dígitos = até 9.999.999.999,99999)
    const limiteDigitos = 13 + this.casasDecimais;
    if (this.digitos.length >= limiteDigitos) {
      return;
    }

    this.digitos = this.digitos + digito;
    this.atualizarValor();
  }

  /**
   * Remove o último dígito do buffer e atualiza o valor.
   */
  private removerUltimoDigito(): void {
    if (this.digitos.length === 0) {
      return;
    }

    this.digitos = this.digitos.slice(0, -1);
    this.atualizarValor();
  }

  /**
   * Converte o buffer de dígitos em valor numérico e atualiza o ngModel.
   */
  private atualizarValor(): void {
    const valor = this.digitosParaValor(this.digitos);

    if (this.ngModel) {
      this.ngModel.viewToModelUpdate(valor);
      this.ngModel.control?.setValue(valor, { emitEvent: false });
    }
  }

  /**
   * Converte valor numérico em string de dígitos para o buffer.
   * Ex: 12.34 com 2 casas decimais → "1234"
   *
   * Para valor === 0, retornamos '' (buffer vazio) para que o usuario
   * comece a digitar do zero — o p-inputNumber ja exibe "0" no campo
   * via formatValue(0), entao nao ha confusao visual.
   */
  private valorParaDigitos(valor: number | null | undefined): string {
    if (valor == null || isNaN(Number(valor)) || Number(valor) === 0) {
      return '';
    }

    const num = Math.round(Math.abs(Number(valor)) * Math.pow(10, this.casasDecimais));
    return String(num);
  }

  /**
   * Converte string de dígitos em valor numérico.
   * Ex: "1234" com 2 casas decimais → 12.34
   */
  private digitosParaValor(digitos: string): number {
    if (!digitos || digitos === '') {
      return 0;
    }

    const numero = parseInt(digitos, 10);
    return numero / Math.pow(10, this.casasDecimais);
  }

  /**
   * Teclas que devem passar sem intervenção da diretiva.
   */
  private ehTeclaPermitida(tecla: string): boolean {
    const teclasSistema = [
      'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Home', 'End', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12',
    ];
    return teclasSistema.includes(tecla);
  }
}
